import { MenuProps, Modal } from 'antd';
import './teamConfig.css';
import { useContext, useEffect, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import TeamConfigTable from './TeamConfigTable';
import TeamConfigFilter from './TeamConfigFilter';
import { MyContext } from '../../context/myContext';
import useApi from '../../hooks/useApi';
import teamConfig from '../../api/TeamConfigApi';
import CreateTeam from './CreateTeam';
import teamMemberConfig from '../../api/TeamMembersConfig';

type TeamMember = {
    domain: string;
    first_name: string;
    id: number;
    last_name: string | null;
    product_id: number;
    product_name: string;
    team_id: number;
    team_member_email: string;
    team_name: string;
    tool_ids: any[]
};

const TeamConfig = () => {
    const menuCSS: React.CSSProperties = { borderRight: 0, background: '#F8F8F8' };
    const [teamList, setTeamList] = useState<any[]>([]);
    const { selectedProduct, user } = useContext<any>(MyContext);

    const getTeamsApi = useApi(teamConfig.getTeams)
    const saveTeamApi = useApi(teamConfig.saveTeam)
    const deleteTeamApi = useApi(teamConfig.deleteTeam)

    const getTeamMemberApi = useApi(teamMemberConfig.getTeamMember)
    const saveTeamMemberApi = useApi(teamMemberConfig.saveTeamMember)

    const [isNewTeamModalOpen, setNewTeamModalOpen] = useState<boolean>(false);
    const [selectedTeam, setSelectedTeam] = useState<any>();
    const [teamMemberList, setTeamMemberList] = useState<TeamMember[]>([]);

    const getTeams = async (product: string) => {
        const response = await getTeamsApi.request(product);
        if (response?.data) {
            setTeamList(response?.data?.data)
            if (response?.data?.data?.length) {
                setSelectedTeam(response?.data?.data[0])
                getTeamMembers(selectedProduct, response?.data?.data[0]?.name)
            }
        }
    }

    const getTeamMembers = async (product: string, team: string) => {
        const response = await getTeamMemberApi.request(product, team);
        if (response?.data) {
            const list = response?.data?.data?.map((item: TeamMember) => {
                return {
                    ...item,
                    team_member_email: item?.team_member_email?.split("@")[0],
                    domain: `@${getOrganization()}`,
                    team_name: team,
                    product_name: product,
                    ...item?.tool_ids
                }
            })
            setTeamMemberList(list)
        }
    }

    const saveTeam = async (teamName: string) => {
        const obj = {
            id: 0,
            name: teamName
        }
        const response = await saveTeamApi.request(selectedProduct, obj);
        if (response?.data) {
            getTeams(selectedProduct)
        }
    }

    const deleteTeam = async (team: any) => {
        const response = await deleteTeamApi.request(selectedProduct, team?.name);
        if (response?.data) {
            getTeams(selectedProduct)
        }
    }

    useEffect(() => {
        if (selectedProduct) {
            getTeams(selectedProduct)
        }
    }, [selectedProduct])

    const getOrganization = () => {
        const org = user?.email?.split("@")[1];
        return org;
    }

    const handleDeleteAction = (item: any) => {
        Modal.confirm({
            title: `Are you sure you want to delete ${item?.name}?`,
            content: "This action cannot be undone.",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                deleteTeam(item)
            },
        });
    };

    const handleDelete = (item: any) => {
        handleDeleteAction(item)
    }

    const menuItems = teamList.map((item) => ({
        ...item,
        key: item?.id,
        label: (
            <div className='flex w-full justify-between items-center'>
                <div>{item.name}</div>
                <div>
                    <DeleteOutlined
                        onClick={(e) => {
                            e.stopPropagation(); // prevent menu item click
                            handleDelete(item);
                        }}
                    />
                </div>
            </div>
        ),
    }));

    const handleSelectTeam = (team: any) => {
        const selectedTeam = teamList?.find((item: any) => item.id === Number(team));
        setSelectedTeam(selectedTeam)
        getTeamMembers(selectedProduct, selectedTeam?.name)
    }

    const handleNewTeam = (team: any) => {
        setNewTeamModalOpen(true);
    }

    const afterUpdate = () => {
        getTeamMembers(selectedProduct, selectedTeam?.name)
    }

    const closeModal = () => {
        setNewTeamModalOpen(false);
    }

    const saveNewTeam = (name: string) => {
        saveTeam(name)
        setNewTeamModalOpen(false);
    }

    const saveTeamMember = async (data: any) => {
        try {
            const obj = {
                ...data,
                id: typeof data?.id === 'number' ? data?.id : 0,
                team_member_email: `${data?.team_member_email}${data?.domain}`,
                first_name: data?.first_name,
                last_name: data?.last_name,
                tool_ids: JSON.stringify(data.tool_ids)
            }
            const response = await saveTeamMemberApi.request(obj?.product_name, obj?.team_name, obj)
            if(response?.data){
                getTeamMembers(obj?.product_name, obj?.team_name)   
            }
        }
        catch(err){

        }
    }

    return (
        <div className="team-config-container">
            <TeamConfigFilter
                menuItems={menuItems}
                menuCSS={menuCSS}
                handleSelectTeam={handleSelectTeam}
                handleNewTeam={handleNewTeam}
                selectedTeam={selectedTeam}
            />
            <div className="content-area mt-4">
                <TeamConfigTable
                    teamMemberList={teamMemberList}
                    afterUpdate={afterUpdate}
                    getOrganization={getOrganization}
                    selectedTeam={selectedTeam}
                    saveTeamMember={saveTeamMember}
                />
            </div>
            {isNewTeamModalOpen
                && <CreateTeam
                    isNewTeamModalOpen={isNewTeamModalOpen}
                    closeModal={closeModal}
                    saveNewTeam={saveNewTeam}
                    teamList={teamList}
                />}
        </div>
    )
}

export default TeamConfig;